/**
 * AI Passport Photo Maker — quality tiers + optional post-result add-ons.
 * Toggle `enabled` (or remove a row) to ship/unship features without rewriting the modal.
 */
import { creditCostForTool } from "../../../../../data/pricingModal";

export const PASSPORT_QUALITY_TIERS = [
  {
    id: "low",
    label: "Low Quality",
    credits: 5,
    eta: "~15s",
    hint: "Fast preview",
    enabled: true,
  },
  {
    id: "medium",
    label: "Medium Quality",
    credits: 10,
    eta: "~30s",
    hint: "Balanced",
    enabled: true,
  },
  {
    id: "high",
    label: "High Quality",
    credits: 25,
    eta: "~65s",
    hint: "Best details",
    enabled: true,
  },
];

export const DEFAULT_PASSPORT_QUALITY_ID = "medium";

/** Must match BgRemoveBackend PASSPORT_QUALITY_DPI. */
export const PASSPORT_QUALITY_DPI = {
  low: 150,
  medium: 300,
  high: 600,
};

export function passportDpiForQuality(qualityId) {
  return PASSPORT_QUALITY_DPI[qualityId] ?? PASSPORT_QUALITY_DPI[DEFAULT_PASSPORT_QUALITY_ID];
}

/** Compliance line, e.g. 35×45 mm at medium → "413×531 (300 DPI)". */
export function formatPassportQualityLabel(widthMm, heightMm, qualityId) {
  const dpi = passportDpiForQuality(qualityId);
  const pxW = Math.round((Number(widthMm) / 25.4) * dpi);
  const pxH = Math.round((Number(heightMm) / 25.4) * dpi);
  return `${pxW}×${pxH} (${dpi} DPI)`;
}

export const PASSPORT_QUALITY_TASK_KEYS = {
  low: "passport_photo_low",
  medium: "passport_photo_medium",
  high: "passport_photo_high",
};

export const PASSPORT_ADDON_TASK_KEYS = {
  clothing: "passport_addon_clothing",
  retouch: "passport_addon_retouch",
  eyewear: "passport_addon_eyewear",
  smile: "passport_addon_smile",
  hair: "passport_addon_hair",
};

export const PASSPORT_ADDONS = [
  {
    id: "clothing",
    label: "Clothing selection",
    credits: 10,
    enabled: true,
  },
  {
    id: "retouch",
    label: "Face Retouch",
    credits: 10,
    enabled: true,
  },
  {
    id: "eyewear",
    label: "Eyewear Removal",
    credits: 5,
    enabled: true,
  },
  {
    id: "smile",
    label: "Smile Correction",
    credits: 5,
    enabled: true,
  },
  {
    id: "hair",
    label: "Hair Correction",
    credits: 5,
    enabled: true,
  },
];

export function getEnabledPassportQualityTiers(tiers = PASSPORT_QUALITY_TIERS) {
  return (tiers || PASSPORT_QUALITY_TIERS).filter((t) => t.enabled !== false);
}

export function getPassportQualityTier(id, tiers = PASSPORT_QUALITY_TIERS) {
  const enabled = getEnabledPassportQualityTiers(tiers);
  return (
    enabled.find((t) => t.id === id) ||
    enabled.find((t) => t.id === DEFAULT_PASSPORT_QUALITY_ID) ||
    enabled[0] ||
    null
  );
}

export function getEnabledPassportAddons(addons = PASSPORT_ADDONS) {
  return (addons || PASSPORT_ADDONS).filter((a) => a.enabled !== false);
}

export function sumPassportAddonCredits(selectedIds = [], addons = PASSPORT_ADDONS) {
  const enabled = getEnabledPassportAddons(addons);
  const set = new Set(selectedIds);
  return enabled.reduce(
    (sum, addon) => (set.has(addon.id) ? sum + Number(addon.credits || 0) : sum),
    0
  );
}

function costFromPricingTool(tool, audience = "payg") {
  return creditCostForTool(tool, audience);
}

/** Overlay GET /api/pricing tool costs onto quality + addon rows. */
export function applyPassportCostsFromPricing(tools, audience = "payg") {
  const byKey = Object.fromEntries(
    (Array.isArray(tools) ? tools : []).filter((t) => t?.key).map((t) => [t.key, t]),
  );
  const tiers = PASSPORT_QUALITY_TIERS.map((tier) => {
    const cost = costFromPricingTool(byKey[PASSPORT_QUALITY_TASK_KEYS[tier.id]], audience);
    return cost == null || Number.isNaN(cost) ? tier : { ...tier, credits: cost };
  });
  const addons = PASSPORT_ADDONS.map((addon) => {
    const cost = costFromPricingTool(byKey[PASSPORT_ADDON_TASK_KEYS[addon.id]], audience);
    return cost == null || Number.isNaN(cost) ? addon : { ...addon, credits: cost };
  });
  return { tiers, addons };
}

/** Prepare cards: Single + sheet sizes (3-column UI). */
export const DEFAULT_PASSPORT_PREPARE_OPTION_ID = "single";

export const PASSPORT_SHEET_SIZES = [
  {
    id: "4x6",
    label: "4×6",
    widthMm: 101.6,
    heightMm: 152.4,
    enabled: true,
  },
  {
    id: "5x7",
    label: "5×7",
    widthMm: 127,
    heightMm: 177.8,
    enabled: true,
  },
  {
    id: "3.5x5",
    label: "Wallet",
    widthMm: 88.9,
    heightMm: 127,
    enabled: true,
  },
  {
    id: "8x10",
    label: "8×10",
    widthMm: 203.2,
    heightMm: 254,
    enabled: true,
  },
  {
    id: "A4",
    label: "A4",
    widthMm: 210,
    heightMm: 297,
    enabled: true,
  },
  {
    id: "A5",
    label: "A5",
    widthMm: 148,
    heightMm: 210,
    enabled: true,
  },
  {
    id: "A6",
    label: "A6",
    widthMm: 105,
    heightMm: 148,
    enabled: true,
  },
  {
    id: "Letter",
    label: "Letter",
    widthMm: 215.9,
    heightMm: 279.4,
    enabled: true,
  },
  {
    id: "Legal",
    label: "Legal",
    widthMm: 215.9,
    heightMm: 355.6,
    enabled: true,
  },
  {
    id: "A3",
    label: "A3",
    widthMm: 297,
    heightMm: 420,
    enabled: true,
  },
];

export const DEFAULT_PASSPORT_SHEET_SIZE_ID = "A4";

/** @deprecated Prefer prepare options — kept for callers expecting format ids. */
export const PASSPORT_PRINT_FORMATS = [
  {
    id: "single",
    label: "Single",
    hint: "1 copy · digital",
    print_sheet: false,
    enabled: true,
  },
  {
    id: "grid",
    label: "Grid to fit",
    hint: "Print sheet",
    print_sheet: true,
    enabled: true,
  },
];

export const DEFAULT_PASSPORT_PRINT_FORMAT_ID = "single";

export function getEnabledPassportSheetSizes() {
  return PASSPORT_SHEET_SIZES.filter((s) => s.enabled !== false);
}

export function getPassportSheetSize(id) {
  const enabled = getEnabledPassportSheetSizes();
  return (
    enabled.find((s) => s.id === id) ||
    enabled.find((s) => s.id === DEFAULT_PASSPORT_SHEET_SIZE_ID) ||
    enabled[0] ||
    null
  );
}

/** Match PhotoStudio `_build_print_sheet` packing. */
export const PASSPORT_SHEET_MARGIN_MM = 3;
export const PASSPORT_SHEET_GUTTER_MM = 2;

/**
 * Pack country-size photos onto one sheet orientation (mm).
 * Formula mirrors PhotoStudio: floor((sheet - 2*margin + gutter) / (photo + gutter)).
 */
export function packPassportPhotosOnSheet({
  sheetWidthMm,
  sheetHeightMm,
  photoWidthMm = 35,
  photoHeightMm = 45,
  marginMm = PASSPORT_SHEET_MARGIN_MM,
  gutterMm = PASSPORT_SHEET_GUTTER_MM,
} = {}) {
  const sw = Math.max(0, Number(sheetWidthMm) || 0);
  const sh = Math.max(0, Number(sheetHeightMm) || 0);
  const pw = Math.max(1, Number(photoWidthMm) || 35);
  const ph = Math.max(1, Number(photoHeightMm) || 45);
  const margin = Math.max(0, Number(marginMm) || 0);
  const gutter = Math.max(0, Number(gutterMm) || 0);

  const cols = Math.max(
    0,
    Math.floor((sw - 2 * margin + gutter) / (pw + gutter))
  );
  const rows = Math.max(
    0,
    Math.floor((sh - 2 * margin + gutter) / (ph + gutter))
  );

  if (cols < 1 || rows < 1) {
    return {
      cols: 0,
      rows: 0,
      count: 0,
      sheetWidthMm: sw,
      sheetHeightMm: sh,
      photoWidthMm: pw,
      photoHeightMm: ph,
      marginMm: margin,
      gutterMm: gutter,
    };
  }

  return {
    cols,
    rows,
    count: cols * rows,
    sheetWidthMm: sw,
    sheetHeightMm: sh,
    photoWidthMm: pw,
    photoHeightMm: ph,
    marginMm: margin,
    gutterMm: gutter,
  };
}

/**
 * Pick portrait vs landscape sheet orientation for maximum printouts.
 * Photos stay upright (country w_mm × h_mm); only the page rotates.
 * Tie → portrait (standard print default).
 *
 * @returns {{
 *   cols: number, rows: number, count: number,
 *   orientation: 'portrait' | 'landscape',
 *   sheetWidthMm: number, sheetHeightMm: number,
 *   photoWidthMm: number, photoHeightMm: number,
 *   marginMm: number, gutterMm: number,
 * }}
 */
export function computePassportSheetGrid({
  sheetWidthMm,
  sheetHeightMm,
  photoWidthMm = 35,
  photoHeightMm = 45,
  marginMm = PASSPORT_SHEET_MARGIN_MM,
  gutterMm = PASSPORT_SHEET_GUTTER_MM,
} = {}) {
  const baseW = Math.max(1, Number(sheetWidthMm) || 210);
  const baseH = Math.max(1, Number(sheetHeightMm) || 297);

  const portrait = packPassportPhotosOnSheet({
    sheetWidthMm: baseW,
    sheetHeightMm: baseH,
    photoWidthMm,
    photoHeightMm,
    marginMm,
    gutterMm,
  });
  const landscape = packPassportPhotosOnSheet({
    sheetWidthMm: baseH,
    sheetHeightMm: baseW,
    photoWidthMm,
    photoHeightMm,
    marginMm,
    gutterMm,
  });

  const useLandscape = landscape.count > portrait.count;
  const best = useLandscape ? landscape : portrait;

  return {
    ...best,
    cols: best.count > 0 ? best.cols : 1,
    rows: best.count > 0 ? best.rows : 1,
    count: best.count > 0 ? best.count : 1,
    orientation: useLandscape ? "landscape" : "portrait",
  };
}

/**
 * Five prepare cards: Single, A4, Letter, A3, 4×6 — with max copy counts.
 */
function formatPrepareSizeMm(widthMm, heightMm) {
  const fmt = (n) => {
    const v = Math.round(Number(n) * 10) / 10;
    return Number.isInteger(v) ? String(v) : String(v);
  };
  return `${fmt(widthMm)}×${fmt(heightMm)}mm`;
}

export function getPassportPrepareOptions({
  photoWidthMm = 35,
  photoHeightMm = 45,
} = {}) {
  const pw = Number(photoWidthMm) || 35;
  const ph = Number(photoHeightMm) || 45;

  const single = {
    id: "single",
    label: "Single",
    hint: "JPEG · digital",
    print_sheet: false,
    sheetSizeId: null,
    sizeLabel: formatPrepareSizeMm(pw, ph),
    copies: 1,
    cols: 1,
    rows: 1,
    orientation: "portrait",
    thumb: "single",
    iconWidthMm: pw,
    iconHeightMm: ph,
  };

  const aSeriesOrder = ["A6", "A5", "A4", "A3"];
  const aSeriesRank = Object.fromEntries(
    aSeriesOrder.map((id, i) => [id, i])
  );
  const usGroupOrder = ["3.5x5", "Letter", "Legal"]; // Wallet, Letter, Legal
  const usGroupRank = Object.fromEntries(
    usGroupOrder.map((id, i) => [id, i])
  );

  const sheets = getEnabledPassportSheetSizes()
    .map((sheet) => {
      const grid = computePassportSheetGrid({
        sheetWidthMm: sheet.widthMm,
        sheetHeightMm: sheet.heightMm,
        photoWidthMm: pw,
        photoHeightMm: ph,
      });
      return {
        id: sheet.id,
        label: sheet.label,
        hint: "Print sheet",
        print_sheet: true,
        sheetSizeId: sheet.id,
        sizeLabel: formatPrepareSizeMm(sheet.widthMm, sheet.heightMm),
        copies: grid.count,
        cols: grid.cols,
        rows: grid.rows,
        orientation: grid.orientation,
        thumb: "grid",
        // Packed page orientation so the icon matches what the canvas shows.
        iconWidthMm: grid.sheetWidthMm,
        iconHeightMm: grid.sheetHeightMm,
      };
    })
    .sort((a, b) => {
      const groupOf = (id) => {
        if (usGroupRank[id] != null) return 1; // Wallet / Letter / Legal
        if (aSeriesRank[id] != null) return 2; // A6 → A3 last
        return 0; // photo sizes first
      };
      const ga = groupOf(a.id);
      const gb = groupOf(b.id);
      if (ga !== gb) return ga - gb;
      if (ga === 1) return usGroupRank[a.id] - usGroupRank[b.id];
      if (ga === 2) return aSeriesRank[a.id] - aSeriesRank[b.id];
      return a.copies - b.copies || a.label.localeCompare(b.label);
    });

  return [single, ...sheets];
}

export function getPassportPrepareOption(id, dims = {}) {
  const options = getPassportPrepareOptions(dims);
  return (
    options.find((o) => o.id === id) ||
    options.find((o) => o.id === DEFAULT_PASSPORT_PREPARE_OPTION_ID) ||
    options[0] ||
    null
  );
}

export function getEnabledPassportPrintFormats() {
  return PASSPORT_PRINT_FORMATS.filter((f) => f.enabled !== false);
}

export function getPassportPrintFormat(id) {
  const enabled = getEnabledPassportPrintFormats();
  return (
    enabled.find((f) => f.id === id) ||
    enabled.find((f) => f.id === DEFAULT_PASSPORT_PRINT_FORMAT_ID) ||
    enabled[0] ||
    null
  );
}
